import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface SocialAssets {
  ogDescription: string;         // 150-160 chars for link previews
  twitterThread: string[];       // Array of tweets (aim for ≤280 chars each)
  linkedInPost: string;          // Long-form professional post
  instagramCaptions: string[];   // Array of caption variants for carousel/feed
  facebookAdCopy: string;        // Facebook/Meta ad body copy
  generatedAt?: string;
}

interface UseSocialAssetsResult {
  isLoading: boolean;
  error: string | null;
  assets: SocialAssets | null;
  generateAssets: (projectId: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for AI-powered social media asset generation.
 * Calls the `generateSocialAssets` Cloud Function with the projectId.
 * Returns platform-specific copy: OG description, Twitter/X thread,
 * LinkedIn post, Instagram captions, and Facebook ad copy.
 */
export function useSocialAssets(): UseSocialAssetsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assets, setAssets] = useState<SocialAssets | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback(() => {
    setError(null);
    setAssets(null);
  }, []);

  const generateAssets = useCallback(async (projectId: string): Promise<void> => {
    if (!projectId) {
      setError("Project ID is required");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const generateFn = httpsCallable<
        { projectId: string },
        { success: boolean; assets: SocialAssets }
      >(functions, "generateSocialAssets");

      const result = await generateFn({ projectId });
      setAssets(result.data.assets);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to generate social assets";
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, error, assets, generateAssets, clearError, reset };
}
